// src/components/common/ScrollableTable.tsx
import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCaretDown, faSquareCaretRight } from '@/icons';
import './ScrollableTable.css';
import { logger } from '@/utils/logger';

interface ScrollableTableProps<T> {
  data: T[];
  hasMore: boolean;
  loadMore: () => void;
  isLoading: boolean;
  columns: {
    header: string;
    render: (item: T, index: number) => React.ReactNode;
    width?: string;
  }[];
  onSelectChange: (selectedItems: T[]) => void;
  renderAction?: (item: T, index: number) => React.ReactNode;
  renderHistoryAction?: (item: T, index: number) => React.ReactNode;
  renderPurchaseHistoryAction?: (item: T, index: number) => React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchDetails?: (item: T) => Promise<any[]>; // Novo: função para fetch assíncrono de detalhes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  detailsColumns?: { header: string; accessor?: string; render?: (row: any) => React.ReactNode; formatter?: (value: any) => string }[]; // Modificado: adiciona render opcional para customização condicional por row, mantendo compatibilidade.
  emptyDetailsMessage?: (item: T) => string; // Novo: função para mensagem customizada quando detalhes vazios, baseada no item
  emptyMessage?: string;
  showCheckboxes?: boolean;
  renderCheckbox?: (item: T) => boolean;
  singleSelect?: boolean;
  isModal?: boolean;
  showTotalItems?: boolean;
  totalItems?: number;
  // Quando o valor desta prop muda, limpa o estado interno de selecao
  // (selectedItems + selectAll). Use para sincronizar reset externo
  // — ex.: ao clicar em "Desistir" no parent, incrementar o contador.
  selectionResetKey?: number | string;
  // OPT-IN (issue #31): seleção "tudo entre páginas".
  //
  // Ausente = comportamento historico intacto: a tabela e dona da selecao e o
  // checkbox de cabecalho marca apenas `data`, ou seja, a pagina carregada.
  //
  // Presente = a tabela vira CONTROLADA para selecao. O conjunto de exclusao
  // mora no parent, por id — e nao aqui — porque a comparacao interna e por
  // REFERENCIA (`selectedItems.includes(item)`), que nao sobrevive a uma nova
  // pagina trazendo o mesmo registro noutro objeto. Neste modo `onSelectChange`
  // nao e chamado pelos checkboxes: quem sabe o que esta selecionado e o parent.
  selecaoEntrePaginas?: {
    /**
     * Convencao de checkbox de cabecalho em tabela, com os tres estados:
     *   'vazio'    — nada selecionado;
     *   'parcial'  — parte selecionada (renderizado como TRACO, nao como vazio);
     *   'marcado'  — tudo selecionado.
     */
    estadoCabecalho: 'vazio' | 'parcial' | 'marcado';
    /** O parent decide se a linha aparece marcada. */
    estaSelecionado: (item: T) => boolean;
    /**
     * Clique no cabecalho. Sem argumento de propósito: com o estado 'parcial' o
     * `e.target.checked` do DOM nao descreve a intencao (um checkbox em traco
     * reporta checked=false, entao clicar nele chega como "marcar"). Quem decide
     * e o parent, a partir do estado que ele mesmo publicou.
     */
    onToggleCabecalho: () => void;
    /** Clique no checkbox de uma linha. */
    onSelecionarItem: (item: T, marcado: boolean) => void;
  };
}

const ScrollableTable = <T,>({
  data,
  hasMore: _hasMore,
  loadMore,
  isLoading,
  columns,
  onSelectChange,
  renderAction,
  renderHistoryAction,
  renderPurchaseHistoryAction,
  fetchDetails,
  detailsColumns,
  emptyDetailsMessage, // Novo: adiciona destructuring para emptyDetailsMessage, corrigindo erro de nome não encontrado
  emptyMessage = 'Nenhum item encontrado.',
  showCheckboxes = true,
  renderCheckbox = () => true,
  singleSelect = false,
  isModal = false,
  showTotalItems = false,
  totalItems,
  selectionResetKey,
  selecaoEntrePaginas,
}: ScrollableTableProps<T>) => {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Reset do estado interno de selecao quando o parent incrementa
  // selectionResetKey (ex.: ao clicar em "Desistir"). Evita checkboxes
  // visualmente marcados mesmo apos limpeza no parent (uncontrolled drift).
  useEffect(() => {
    setSelectedItems([]);
    setSelectAll(false);
  }, [selectionResetKey]);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set()); // Novo: gerencia linhas expandidas
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [detailsData, setDetailsData] = useState<{ [key: number]: any[] }>({}); // Novo: cache de dados de detalhes por ID
  const sentinelRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const cabecalhoCheckboxRef = useRef<HTMLInputElement>(null);

  // O estado "traco" de um checkbox nao existe como atributo em HTML — so pela
  // propriedade `indeterminate` do elemento. Sem a prop opt-in isto sempre grava
  // false, que ja e o default: nenhuma tela existente muda de comportamento.
  useEffect(() => {
    if (cabecalhoCheckboxRef.current) {
      cabecalhoCheckboxRef.current.indeterminate =
        selecaoEntrePaginas?.estadoCabecalho === 'parcial';
    }
  }, [selecaoEntrePaginas?.estadoCabecalho, data]);

  useEffect(() => {
    if (data.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          logger.log('Intersection Observer disparado - Carregando mais itens');
          loadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px 50px 0px',
        root: tableContainerRef.current,
      }
    );
    const currentSentinel = sentinelRef.current;
    if (currentSentinel) observer.observe(currentSentinel);
    return () => {
      if (currentSentinel) observer.unobserve(currentSentinel);
    };
  }, [loadMore, data, isLoading]);

  const handleSelectAllChange = (checked: boolean) => {
    if (singleSelect) return; // Desativa selectAll para seleção única
    setSelectAll(checked);
    const newSelectedItems = checked ? data.filter(item => renderCheckbox(item)) : [];
    setSelectedItems(newSelectedItems);
    onSelectChange(newSelectedItems);
  };

  const handleItemSelectChange = (item: T, checked: boolean) => {
    let newSelectedItems: T[];
    if (singleSelect) {
      // Seleção única: substitui qualquer seleção anterior
      newSelectedItems = checked ? [item] : [];
    } else {
      newSelectedItems = checked
        ? [...selectedItems, item]
        : selectedItems.filter((selectedItem) => selectedItem !== item);
    }
    setSelectedItems(newSelectedItems);
    setSelectAll(!singleSelect && newSelectedItems.length === data.filter(item => renderCheckbox(item)).length);
    onSelectChange(newSelectedItems);
  };

  useEffect(() => {
    // Modo entre-paginas: o cabecalho reflete `todosSelecionados` do parent, e
    // este efeito o sobrescreveria a cada pagina carregada — "selecionar tudo"
    // desmarcaria sozinho no primeiro scroll, porque a pagina nova chega com
    // itens que nao estao em `selectedItems`. Unico desvio do comportamento
    // historico, e so existe quando a prop opt-in esta presente.
    if (selecaoEntrePaginas) return;
    const selectableItems = data.filter(item => renderCheckbox(item));
    // Verifica se todos os itens selecionáveis estão realmente em selectedItems
    const allSelected = selectableItems.length > 0 &&
      selectableItems.every(item => selectedItems.includes(item));
    // Desmarca selectAll se não houver itens selecionados ou se nem todos os selecionáveis estão marcados
    setSelectAll(!singleSelect && selectedItems.length > 0 && allSelected);
  }, [data, selectedItems, renderCheckbox, singleSelect, selecaoEntrePaginas]);

  const toggleExpand = async (item: T) => {
    const id = (item as { id: number }).id; // Assume que T tem 'id' (como RequisicaoItem)
    const isExpanded = expandedRows.has(id);

    if (!isExpanded && fetchDetails && !detailsData[id]) {
      try {
        const data = await fetchDetails(item);
        setDetailsData(prev => ({ ...prev, [id]: data }));
      } catch (err) {
        logger.error('Erro ao carregar detalhes:', err);
      }
    }

    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (isExpanded) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  // Novo: calcula total de colunas para colSpan
  const totalCols = columns.length + (showCheckboxes ? 1 : 0) + (renderAction ? 1 : 0) + (renderHistoryAction ? 1 : 0) + (renderPurchaseHistoryAction ? 1 : 0) + (fetchDetails ? 1 : 0);

  return (
    <div className={`table-container ${isModal ? 'modal-table' : ''}`} ref={tableContainerRef}>
      {data.length === 0 ? (
        <p>{emptyMessage}</p>
      ) : (
        <table className="scrollable-table">
          <thead>
            <tr>{/* Removidos quebras de linha e espaços extras; motivo: evita nós de texto em branco dentro de <tr>, corrigindo erro de hydration. */}
              {/* Exibe a coluna de checkboxes se showCheckboxes for true */}{showCheckboxes && (
                <th className="checkbox-column">{!singleSelect && (
                    <input
                      ref={cabecalhoCheckboxRef}
                      type="checkbox"
                      checked={
                        selecaoEntrePaginas
                          ? selecaoEntrePaginas.estadoCabecalho === 'marcado'
                          : selectAll
                      }
                      onChange={(e) =>
                        selecaoEntrePaginas
                          ? selecaoEntrePaginas.onToggleCabecalho()
                          : handleSelectAllChange(e.target.checked)
                      }
                    />
                  )}</th>
              )}{renderAction && <th className="action-column"></th>}{renderHistoryAction && <th className="action-column"></th>}{renderPurchaseHistoryAction && <th className="action-column"></th>}{fetchDetails && <th className="action-column"></th>}{columns.map((column, index) => (
                <th key={index} style={column.width ? { width: column.width } : undefined}>{column.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => {
              const id = (item as { id: number }).id;
              const isExpanded = expandedRows.has(id);
              return (
                <React.Fragment key={index}>
                  <tr>{/* Removidos quebras de linha e espaços extras; motivo: evita nós de texto em branco dentro de <tr>, corrigindo erro de hydration. */}
                    {showCheckboxes && (
                      <td className="checkbox-column">{renderCheckbox(item) && (
                          <input
                            type="checkbox"
                            checked={
                              selecaoEntrePaginas
                                ? selecaoEntrePaginas.estaSelecionado(item)
                                : selectedItems.includes(item)
                            }
                            onChange={(e) =>
                              selecaoEntrePaginas
                                ? selecaoEntrePaginas.onSelecionarItem(item, e.target.checked)
                                : handleItemSelectChange(item, e.target.checked)
                            }
                          />
                        )}</td>
                    )}{renderAction && (
                      <td className="action-column">{renderAction(item, index)}</td>
                    )}{renderHistoryAction && (
                      <td className="action-column">{renderHistoryAction(item, index)}</td>
                    )}{renderPurchaseHistoryAction && (
                      <td className="action-column">{renderPurchaseHistoryAction(item, index)}</td>
                    )}{fetchDetails && (
                      <td className="action-column"><button className="action-button" onClick={() => toggleExpand(item)}>
                          <FontAwesomeIcon icon={isExpanded ? faSquareCaretDown : faSquareCaretRight} />
                        </button></td>
                    )}{columns.map((column, colIndex) => (
                      <td key={colIndex}>{column.render(item, index)}</td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className="details-row">
                      <td colSpan={totalCols}>
                        {detailsData[id] ? (
                          detailsData[id].length > 0 ? (
                            <table className="sub-table">
                              <thead>
                                <tr>{detailsColumns?.map((col, colIdx) => (
                                    <th key={colIdx}>{col.header}</th>
                                  ))}</tr>
                              </thead>
                              <tbody>
                                {detailsData[id].map((detail, detailIdx) => (
                                  <tr key={detailIdx}>{detailsColumns?.map((col, colIdx) => {
                                      let cellContent: React.ReactNode;
                                      if (col.render) {
                                        cellContent = col.render(detail);
                                      } else {
                                        const value = col.accessor ? detail[col.accessor] : null;
                                        cellContent = col.formatter ? col.formatter(value) : value;
                                      }
                                      return (
                                        <td key={colIdx}>{cellContent == null || cellContent === '' ? 'Não informado' : cellContent}</td>
                                      );
                                    })}</tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="empty-details-message">{emptyDetailsMessage ? emptyDetailsMessage(item) : 'Nenhum dado encontrado'}</p>
                          )
                        ) : (
                          <p>Carregando detalhes...</p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            <tr>
              <td colSpan={totalCols}>
                <div ref={sentinelRef} style={{ height: '0.1px' }} />
              </td>
            </tr>
          </tbody>
          {showTotalItems && totalItems !== undefined && (
            <tfoot>
              <tr>
                <td colSpan={totalCols} className="total-items-footer">
                  <span className="total-items">
                    {totalItems === 0 || totalItems === null ? '' : totalItems === 1 ? 'Apenas 1 item' : `${totalItems} itens no total`}
                  </span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
      {isLoading && <p>Carregando mais itens...</p>}
    </div>
  );
};

export default ScrollableTable;